import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';   // FW-3A
import {
    Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, Paper, Stack,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { ExpenseAdvanceService } from '../../services/expense-advance.service';
import { AccountService } from '../../services/account.service';
import { ExpenseAdvanceDTO } from '../../interfaces/expense.interface';
import { Account } from '../../interfaces/accounting.interface';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { useToast } from '../../context/ToastContext';
import { useEmployeeNames } from '../../hooks/useEmployeeNames';
import { advanceStatusLabel, formatAmount } from '../../utils/expenseDisplay';
import { depositAccountOptions } from '../../utils/postingAccounts';

const PendingApprovalAdvances: React.FC = () => {
    const [requested, setRequested] = useState<ExpenseAdvanceDTO[]>([]);
    const [approved, setApproved] = useState<ExpenseAdvanceDTO[]>([]);
    const employeeName = useEmployeeNames([...requested, ...approved]);
    const [loading, setLoading] = useState(true);
    const [bankAccounts, setBankAccounts] = useState<Account[]>([]);
    const [payTarget, setPayTarget] = useState<ExpenseAdvanceDTO | null>(null);
    const [payAccountId, setPayAccountId] = useState('');
    const [busy, setBusy] = useState(false);
    const toast = useToast();

    useEffect(() => {
        load();
        loadAccounts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = async () => {
        setLoading(true);
        try {
            const page = await ExpenseAdvanceService.listPendingApproval();
            // Backend filters by REQUESTED status; for APPROVED-but-unpaid we'd need a separate
            // endpoint. For v1, show the list as-is.
            setRequested(page.content.filter(a => a.status === 'REQUESTED'));
            setApproved(page.content.filter(a => a.status === 'APPROVED'));
        } catch (e: any) {
            toast.showApiError(e, 'Failed to load advances');
        } finally {
            setLoading(false);
        }
    };

    const loadAccounts = async () => {
        try {
            setBankAccounts(depositAccountOptions(await AccountService.getAccountsByType('ASSET')));
        } catch {
            // optional — only needed for Pay dialog
        }
    };

    const handleAction = async (id: string, action: 'approve' | 'reject') => {
        setBusy(true);
        try {
            const result = action === 'approve' ? await ExpenseAdvanceService.approve(id) : await ExpenseAdvanceService.reject(id);
            // FW-3A: an approve that a workflow took over comes back still REQUESTED, with its approval request
            if (action === 'approve' && result?.status === 'REQUESTED') {
                toast.showSuccess('Sent for approval: a workflow decides this requisition (see Approvals)');
            } else {
                toast.showSuccess(action === 'approve' ? 'Advance approved' : 'Advance rejected');
            }
            load();
        } catch (e: any) {
            toast.showApiError(e, `${action} failed`);
        } finally {
            setBusy(false);
        }
    };

    const handlePay = async () => {
        if (!payTarget || !payAccountId) {
            toast.showError('Pick a cash/bank account');
            return;
        }
        setBusy(true);
        try {
            await ExpenseAdvanceService.pay(payTarget.id, payAccountId);
            toast.showSuccess('Advance paid');
            setPayTarget(null);
            setPayAccountId('');
            load();
        } catch (e: any) {
            toast.showApiError(e, 'Payment failed');
        } finally {
            setBusy(false);
        }
    };

    const renderTable = (title: string, rows: ExpenseAdvanceDTO[], showPay: boolean) => (
        <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>{title}</Typography>
            <TableContainer component={Paper} data-help-section="expense.advanceApprovals.table">
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="expense.advanceApprovals.col.employee">Employee</TableCell>
                            <TableCell align="right" data-help="expense.advanceApprovals.col.amount">Amount</TableCell>
                            <TableCell data-help="expense.advanceApprovals.col.reason">Reason</TableCell>
                            <TableCell data-help="expense.advanceApprovals.col.status">Status</TableCell>
                            <TableCell align="right" data-help="expense.advanceApprovals.col.actions">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map(a => (
                            <TableRow key={a.id} hover>
                                <TableCell data-help="expense.advanceApprovals.col.employee">{employeeName(a)}</TableCell>
                                <TableCell align="right" data-help="expense.advanceApprovals.col.amount">{formatAmount(a.amount)}</TableCell>
                                <TableCell data-help="expense.advanceApprovals.col.reason" sx={{ whiteSpace: 'normal', minWidth: 140 }}>{a.reason}</TableCell>
                                <TableCell data-help="expense.advanceApprovals.col.status"><Chip size="small" label={advanceStatusLabel(a.status)} /></TableCell>
                                <TableCell align="right">
                                    {/* ---- FW-3A: a requisition under a workflow is decided in the approvals inbox ---- */}
                                    {a.status === 'REQUESTED' && a.approvalRequestId && (
                                        <Button size="small" data-help="expense.advanceApprovals.action.decideInApprovals" component={RouterLink} to={`/approvals?request=${a.approvalRequestId}`}>
                                            Decide in Approvals
                                        </Button>
                                    )}
                                    {/* ---- end FW-3A ---- */}
                                    {a.status === 'REQUESTED' && !a.approvalRequestId && (
                                        <>
                                            <Button data-help="expense.advanceApprovals.action.approve" size="small" onClick={() => handleAction(a.id, 'approve')} disabled={busy}>Approve</Button>
                                            <Button data-help="expense.advanceApprovals.action.reject" size="small" color="error" onClick={() => handleAction(a.id, 'reject')} disabled={busy}>Reject</Button>
                                        </>
                                    )}
                                    {showPay && a.status === 'APPROVED' && (
                                        <Button data-help="expense.advanceApprovals.action.pay" size="small" variant="contained" onClick={() => setPayTarget(a)} disabled={busy}>
                                            Pay
                                        </Button>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                        {rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                                        No items.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );

    return (
        <Box sx={{ p: 3 }} data-help-page="expense.advanceApprovals.page">
            <Typography variant="h5" sx={{ mb: 2 }}>Cash Advance Approvals</Typography>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : (
                <>
                    {renderTable('Awaiting approval', requested, false)}
                    {renderTable('Approved — awaiting payment', approved, true)}
                </>
            )}
            <Dialog open={Boolean(payTarget)} onClose={() => setPayTarget(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Pay advance</DialogTitle>
                <DialogContent dividers>
                    {payTarget && (
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <Typography>Amount: <strong>{formatAmount(payTarget.amount)}</strong></Typography>
                            <AccountAutocomplete data-help="expense.advanceApprovals.payAccount"
                                accounts={bankAccounts}
                                value={payAccountId}
                                onChange={setPayAccountId}
                                label="Pay from account"
                            />
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button data-help="expense.advanceApprovals.payCancel" onClick={() => setPayTarget(null)} disabled={busy}>Cancel</Button>
                    <Button variant="contained" data-help="expense.advanceApprovals.payConfirm" onClick={handlePay} disabled={busy}>
                        {busy ? 'Paying…' : 'Pay now'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PendingApprovalAdvances;
